import React, { createContext, useRef } from "react";
import { useFormikContext } from "formik";
import { useUpdateEffect } from "ahooks";

export const InnerContext = createContext({});

const FormInner = React.forwardRef(
  (
    { children, action, form, disabled, className, style, onValuesChange },
    ref
  ) => {
    const innerContextRef = useRef({});
    const valuesRef = useRef({});
    const changedName = useRef(null);
    const formikProps = useFormikContext();

    const getFieldValue = field => {
      return valuesRef.current[field];
    };

    const getFieldsValue = () => {
      return valuesRef.current;
    };

    const setFieldValue = (field, value, shouldValidate) => {
      valuesRef.current[field] = value;
      onValuesChange({ [field]: value }, valuesRef.current);
      return formikProps.setFieldValue(field, value, shouldValidate);
    };

    const setFieldsValue = values => {
      valuesRef.current = values;
      onValuesChange(values, values);
      return formikProps.resetForm({ values });
    };

    const resetForm = nextState => {
      if (nextState.values) {
        valuesRef.current = nextState.values;
        onValuesChange(nextState.values, nextState.values);
      }
      return formikProps.resetForm(nextState);
    };

    const setFormikState = (state, cb) => {
      if (state.values) {
        valuesRef.current = state.values;
        onValuesChange(state.values, state.values);
      }
      return formikProps.setFormikState(state, cb);
    };

    const handleReset = e => {
      valuesRef.current = formikProps.initialValues;
      onValuesChange(valuesRef.current, valuesRef.current);
      return formikProps.handleReset(e);
    };

    if (form && "current" in form) {
      form.current = formikProps;
      form.current.getFieldValue = getFieldValue;
      form.current.getFieldsValue = getFieldsValue;
      form.current.setFieldValue = setFieldValue;
      form.current.setFieldsValue = setFieldsValue;
      form.current.resetForm = resetForm;
      form.current.setFormikState = setFormikState;
      form.current.handleReset = handleReset;
    }

    innerContextRef.current.onValuesChange = e => {
      if (onValuesChange) {
        const { name } = e.target || {};
        changedName.current = name;
      }
    };

    innerContextRef.current.getFieldsValue = getFieldsValue;
    innerContextRef.current.disabled = disabled;

    useUpdateEffect(() => {
      const fieldName = changedName.current;
      const values = formikProps.values;
      if (onValuesChange && fieldName) {
        if (values[fieldName] !== valuesRef.current[fieldName]) {
          onValuesChange({ [fieldName]: values[fieldName] }, values);
        }
      }
      valuesRef.current = values;
    }, [formikProps.values]);

    return (
      <InnerContext.Provider value={innerContextRef.current}>
        <form
          onSubmit={formikProps.handleSubmit}
          ref={ref}
          onReset={handleReset}
          action={action}
          className={className}
          style={style}
        >
          {children}
        </form>
      </InnerContext.Provider>
    );
  }
);

export default FormInner;
